import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActiveBling, blingLabel } from "../lib/blingContext";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Search, CheckCircle2, AlertTriangle,
  BadgePercent, KeyRound, Plug, Unlock, FileText, History, X,
  Zap, Settings, Play, RotateCw, Trash2, Store, Plus,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Canonical marketplaces — must stay in sync with backend
// ``server.py::CANONICAL_MARKETPLACES``. This is the SOLE list of buckets
// the tenant can map their Bling store names to. Order is display order.
// ---------------------------------------------------------------------------
const CANONICAL_MARKETPLACES = [
  "Shopee",
  "Mercado Livre",
  "Amazon",
  "Magalu",
  "TikTok",
  "Shein",
  "Temu",
  "Aliexpress",
  "Desconhecido",
];

// ---------------------------------------------------------------------------
// Bling v3 field access helpers — v3 uses camelCase with nested objects.
// ---------------------------------------------------------------------------
const pick = (obj, ...keys) => {
  for (const k of keys) {
    const parts = k.split(".");
    let v = obj;
    for (const p of parts) v = v?.[p];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

const fmtDate = (raw) => {
  if (!raw) return "—";
  let d = null;
  if (/^\d{4}-\d{2}-\d{2}/.test(String(raw))) d = new Date(String(raw));
  else if (/^\d{2}\/\d{2}\/\d{4}/.test(String(raw))) {
    const [dd, mm, yy] = String(raw).split(/[/ ]/);
    d = new Date(`${yy}-${mm}-${dd}`);
  }
  if (!d || isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("pt-BR");
};

// Bling situações are tenant-configurable. The backend enriches each
// pedido's situacao with ``nome`` (resolved via /situacoes/modulos). We
// prefer the real name; fall back to a friendly map for common ``valor``
// codes if the enrichment couldn't run.
const FALLBACK_SITUACOES = {
  0: "Em aberto",
  6: "Em aberto",
  9: "Atendido",
  12: "Aprovado",
  15: "Em andamento",
  16: "Venda agenciada",
  20: "Cancelado",
};

const SITUACAO_COLORS = [
  { match: /aprovad/i, bg: "#DCFCE7", fg: "#166534" },
  { match: /atendid|conclu[ií]d|entregu/i, bg: "#D1FAE5", fg: "#065F46" },
  { match: /aberto|aguard|pendent/i, bg: "#FEF3C7", fg: "#92400E" },
  { match: /andament|preparand|separ/i, bg: "#DBEAFE", fg: "#1E40AF" },
  { match: /cancel|devolv|estorn/i, bg: "#FEE2E2", fg: "#991B1B" },
  { match: /agenciad|verifica/i, bg: "#E0E7FF", fg: "#3730A3" },
];

const situacaoMeta = (situacao) => {
  if (!situacao) return { label: "—", bg: "#F1F5F9", fg: "#475569" };
  const label =
    situacao.nome ||
    FALLBACK_SITUACOES[situacao.valor] ||
    `Situação ${situacao.valor ?? "?"}`;
  const color = SITUACAO_COLORS.find((c) => c.match.test(label));
  return {
    label,
    bg: color?.bg || "#F1F5F9",
    fg: color?.fg || "#475569",
  };
};

// ---------------------------------------------------------------------------

export default function BlingDiscount() {
  const { user } = useAuth();
  // Multi-Bling: reads the tenant's active Bling from the global sidebar
  // selector via ``useActiveBling()``. Every backend call scopes to the
  // chosen ``integration_id`` so state (orders, history, failures,
  // auto-billing config) is fully isolated per Bling.
  const {
    blings: ctxBlings,
    activeBling: ctxActiveBling,
    activeId: ctxActiveId,
    setActiveId: ctxSetActiveId,
    refresh: refreshBlings,
  } = useActiveBling();
  const blings = ctxBlings;
  const selectedBlingId = ctxActiveId;
  const setSelectedBlingId = ctxSetActiveId;
  const integration = ctxActiveBling;
  const selectedCompany = integration?.company_id || "";
  const [bootLoading, setBootLoading] = useState(true);
  const [companies, setCompanies] = useState([]);

  const [orders, setOrders] = useState([]);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMarketplace, setActiveMarketplace] = useState("__all__");

  const [discountPct, setDiscountPct] = useState("");
  const [applying, setApplying] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [appliedLocal, setAppliedLocal] = useState({});

  // Invoice history dialog state
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyDateFrom, setHistoryDateFrom] = useState(iso(new Date(today.getTime() - 30 * 86400000)));
  const [historyDateTo, setHistoryDateTo] = useState(iso(today));
  const [historyQuery, setHistoryQuery] = useState("");

  // Auto-billing configuration + status
  const [autoConfig, setAutoConfig] = useState(null);      // {enabled, rules, last_run_at, last_run_stats}
  const [autoConfigOpen, setAutoConfigOpen] = useState(false);
  const [storeMappingsOpen, setStoreMappingsOpen] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  // Confirmation card shown when user clicks the ON toggle
  const [autoStartConfirm, setAutoStartConfirm] = useState(false);

  // Invoice failures (SEFAZ down, transient errors) + retry engine
  const [failures, setFailures] = useState({ items: [], count: 0, needs_manual_count: 0, retrying_count: 0, max_attempts: 5 });
  const [failuresOpen, setFailuresOpen] = useState(false);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cRes = await api.get("/companies");
        if (!alive) return;
        setCompanies(cRes.data || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar dados");
      } finally {
        if (alive) setBootLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Persist Bling selection is handled by the global context (no-op here).

  // When user switches between Blings, RESET all per-Bling state so we
  // never mix data across tenants. Fresh fetches (orders, autoConfig,
  // failures) are triggered by the existing effects that key on `integration`.
  useEffect(() => {
    setOrders([]);
    setFetched(false);
    setSelected(new Set());
    setAppliedLocal({});
    setActiveMarketplace("__all__");
    setResults(null);
    setWarning(null);
    setSearchQuery("");
    setDiscountPct("");
    setAutoConfig(null);
    setFailures({ items: [], count: 0, needs_manual_count: 0, retrying_count: 0, max_attempts: 5 });
    setHistoryItems([]);
    setAutoStartConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlingId]);

  // Auto-fetch orders whenever the ACTIVE Bling changes — either at
  // first mount (integration resolves after the context loads) OR when
  // the user picks a different Bling in the sidebar / on-page tabs.
  // Keying on ``integration?.id`` (instead of the whole ``integration``
  // object) avoids spurious refetches when only the nickname or another
  // non-identity field is edited, while still firing on every real
  // account switch. No ``fetched`` guard — the reset effect above
  // clears the list first, and this effect runs on the NEXT render
  // after the id change, so a fresh fetch is always what the user wants
  // (instant "click Luma → see Luma orders" behaviour).
  useEffect(() => {
    if (bootLoading) return;
    if (!integration?.id) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootLoading, integration?.id]);

  const fetchOrders = async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) {
      setLoading(true);
      setResults(null);
    }
    try {
      const q = new URLSearchParams();
      if (selectedCompany) q.append("company_id", selectedCompany);
      // Multi-Bling isolation: always scope by the active Bling so the
      // backend never serves pedidos from another Bling by mistake.
      if (integration?.id) q.append("integration_id", integration.id);
      const { data } = await api.get(`/bling/orders?${q.toString()}`);
      const list = Array.isArray(data) ? data : [];
      // Extract per-response warning attached to first item by backend
      const firstWarn = list[0]?._bling_warning || null;
      if (firstWarn) delete list[0]._bling_warning;
      if (!silent) setWarning(firstWarn);
      setOrders(list);
      setFetched(true);
      if (!silent) {
        setSelected(new Set());
        setAppliedLocal({});
        setActiveMarketplace("__all__");
        toast.success(`${list.length} pedido(s) carregado(s)`);
      }
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Erro ao buscar pedidos");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const pctNum = parseFloat(String(discountPct).replace(",", "."));
  const validPct = Number.isFinite(pctNum) && pctNum > 0 && pctNum <= 100;

  const applyDiscount = async () => {
    if (!validPct || selected.size === 0) return;
    // Filter out pedidos that already had a discount applied by Vyntric —
    // avoids re-hitting the backend with pedidos we know are blocked, and
    // makes the UX cleaner (user sees only "real" results in the panel).
    const alreadyDiscountedIds = new Set(
      allRows.filter((r) => r.hasDiscount).map((r) => r.id)
    );
    const ids = Array.from(selected).filter((id) => !alreadyDiscountedIds.has(id));
    const skipped = selected.size - ids.length;
    if (ids.length === 0) {
      toast.info(
        skipped > 0
          ? `Todos os ${skipped} pedido(s) selecionado(s) já tiveram desconto aplicado — nada a fazer.`
          : "Nenhum pedido válido selecionado."
      );
      return;
    }
    if (skipped > 0) {
      toast.info(`${skipped} pedido(s) já com desconto foram ignorados.`);
    }
    setApplying(true);
    setResults(null);
    try {
      const { data } = await api.post("/bling/orders/apply-discount", {
        order_ids: ids,
        discount_pct: pctNum,
        company_id: selectedCompany || null,
        integration_id: integration?.id || null,
      });
      setResults(data);
      // Merge fresh pedido data returned by Bling into local state — so the
      // "Valor final" column reflects EXACTLY what the ERP has stored,
      // instead of us trying to reproduce Bling's internal formula.
      // We preserve the ORIGINAL total on the record so the "Valor original"
      // column still shows the pre-discount amount.
      const freshMap = data.fresh_orders || {};
      if (Object.keys(freshMap).length > 0) {
        setOrders((prev) => prev.map((o) => {
          const fresh = freshMap[o.id];
          if (!fresh) return o;
          // Snapshot the TRUE original (pre-discount) total. Prefer the
          // detail-based breakdown (totalProdutos + frete + outrasDespesas)
          // over ``o.total`` which may already reflect a marketplace
          // discount coming from Bling.
          const tp = Number(o.totalProdutos ?? 0);
          const fr = Number(o.frete ?? 0);
          const od = Number(o.outrasDespesas ?? 0);
          const origFromDetail = tp > 0 ? (tp + fr + od) : 0;
          const preSnapshot = o._original_total
            ?? (origFromDetail > 0 ? origFromDetail : o.total);
          return {
            ...o,
            ...fresh,
            _original_total: preSnapshot,
          };
        }));
      }
      if (Array.isArray(data.ok)) {
        setAppliedLocal((prev) => {
          const next = { ...prev };
          for (const id of data.ok) next[id] = pctNum;
          return next;
        });
      }
      // Clear selection after applying the discount. This forces the user
      // to re-select pedidos explicitly before invoicing — the two actions
      // (desconto e faturamento) MUST be independent operations.
      setSelected(new Set());
      const failedCount = data.failed?.length || 0;
      if (failedCount === 0) {
        toast.success(`Desconto de ${pctNum}% aplicado em ${data.ok.length} pedido(s)`);
      } else {
        toast.warning(`${data.ok.length} aplicado(s) · ${failedCount} falha(s)`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao aplicar desconto");
    } finally {
      setApplying(false);
    }
  };

  const invoiceSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(
      `Faturar ${selected.size} pedido(s)?\n\n` +
      "Isso emite NF-e no Bling e envia à SEFAZ automaticamente. " +
      "Após autorização, o Bling sincroniza a NF de volta com o marketplace " +
      "de origem via a integração nativa dele."
    )) return;
    setInvoicing(true);
    setResults(null);
    try {
      const ids = Array.from(selected);
      // Build snapshots for each selected order — persisted in the
      // history table for later consultation without hitting Bling again.
      const snapshots = {};
      for (const id of ids) {
        const row = allRows.find((r) => r.id === id);
        if (!row) continue;
        snapshots[String(id)] = {
          numero: String(row.numero || ""),
          numero_loja: String(row.numeroLoja || ""),
          marketplace: row.marketplace || "Desconhecido",
          cliente_nome: row.cliente || "",
          valor_total: Number(row.valor_final ?? row.valor ?? 0),
          discount_pct: Number(row.pct || 0),
        };
      }
      const { data } = await api.post("/bling/orders/invoice", {
        order_ids: ids,
        company_id: selectedCompany || null,
        integration_id: integration?.id || null,
        send_to_sefaz: true,
        order_snapshots: snapshots,
      });
      // Split ok into truly-sent (autorizada) vs generated-but-not-sent
      // (pendente). Pendente entries move to `failed` so the UI shows
      // exactly why the NF didn't reach SEFAZ.
      const okIds = [];
      const failedRich = (data.failed || []).map((f) => ({ numero: f.numero, error: f.error }));
      for (const r of (data.ok || [])) {
        if (r.sent) {
          okIds.push(r.id);
        } else {
          failedRich.push({
            numero: String(r.id),
            error: r.send_error
              ? `NF ${r.nfe_id} criada mas ficou Pendente: ${r.send_error}`
              : `NF ${r.nfe_id} criada mas ficou Pendente na SEFAZ. Reenvie manualmente pelo Bling ou tente novamente.`,
          });
        }
      }
      setResults({ ok: okIds, failed: failedRich, invoice_details: data.ok || [] });
      if (okIds.length > 0) {
        setAppliedLocal((prev) => {
          const next = { ...prev };
          for (const id of okIds) next[id] = next[id] || -1;
          return next;
        });
      }
      // Clear selection so a subsequent click on either action button
      // does not act on stale/invoiced pedidos.
      setSelected(new Set());
      const okCount = okIds.length;
      const failCount = failedRich.length;
      if (failCount === 0) {
        toast.success(`NF-e autorizada em ${okCount} pedido(s).`);
      } else if (okCount === 0) {
        toast.error(`Nenhuma NF autorizada · ${failCount} pendente/falha`);
      } else {
        toast.warning(`${okCount} autorizada(s) · ${failCount} pendente/falha`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao faturar");
    } finally {
      setInvoicing(false);
    }
  };


  const resetTracking = async () => {
    if (!window.confirm(
      "Limpar o registro de descontos aplicados?\n\n" +
      "Isso libera TODOS os pedidos para uma nova tentativa. Use apenas quando " +
      "o desconto NÃO foi persistido no Bling."
    )) return;
    setResetting(true);
    try {
      const { data } = await api.delete("/bling/discounts/tracking");
      toast.success(`Tracking resetado — ${data.deleted} registro(s) removido(s). Recarregando...`);
      setAppliedLocal({});
      setSelected(new Set());
      setResults(null);
      await fetchOrders();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao resetar tracking");
    } finally {
      setResetting(false);
    }
  };

  // -----------------------------------------------------------------
  // Invoice history — persisted for 90 days (TTL by MongoDB)
  // -----------------------------------------------------------------
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const q = new URLSearchParams();
      if (historyDateFrom) q.append("date_from", historyDateFrom);
      if (historyDateTo) q.append("date_to", historyDateTo);
      if (historyQuery.trim()) q.append("q", historyQuery.trim());
      if (selectedCompany) q.append("company_id", selectedCompany);
      if (integration?.id) q.append("integration_id", integration.id);
      const { data } = await api.get(`/bling/invoices/history?${q.toString()}`);
      setHistoryItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao buscar histórico");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setHistoryOpen(true);
    // Defer to next tick so state updates
    setTimeout(() => fetchHistory(), 0);
  };

  // -----------------------------------------------------------------
  // Auto-billing (per-marketplace discount + auto-invoice)
  // -----------------------------------------------------------------
  const fetchAutoConfig = async () => {
    try {
      // Fetch the auto-billing config scoped to the ACTIVE Bling. Each
      // Bling has its own config so multi-store tenants can toggle
      // Matriz ON and Filial OFF independently.
      const params = new URLSearchParams();
      if (selectedCompany) params.append("company_id", selectedCompany);
      if (integration?.id) params.append("integration_id", integration.id);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/bling/auto-billing/config${qs}`);
      setAutoConfig(data);
    } catch (e) {
      // Not critical — silently ignore
      console.error("auto-billing fetch:", e);
    }
  };

  // Load config when integration becomes available
  useEffect(() => {
    if (!integration) return;
    fetchAutoConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration, selectedCompany]);

  // ---- Copy-config-from-another-Bling dialog + diagnostics dialog ----
  // Both features solve the exact "new Bling is added but auto-billing
  // doesn't fire" problem — the copy button retroactively seeds a
  // sibling's config into this Bling; the diagnostics button shows why
  // the last run skipped every pedido (missing rule, disabled rule,
  // wrong status, etc.).
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState("");
  const [copyOverwrite, setCopyOverwrite] = useState(false);
  const [copying, setCopying] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diag, setDiag] = useState(null);

  const openCopyDialog = () => {
    // Default source = first sibling Bling (not the current one)
    const first = blings.find((b) => b.id !== integration?.id);
    setCopySourceId(first?.id || "");
    setCopyOverwrite(false);
    setCopyDialogOpen(true);
  };

  const runCopyConfig = async () => {
    if (!copySourceId || !integration?.id) {
      toast.error("Escolha o Bling de origem.");
      return;
    }
    setCopying(true);
    try {
      const { data } = await api.post("/bling/auto-billing/copy-config", {
        source_integration_id: copySourceId,
        target_integration_id: integration.id,
        overwrite: copyOverwrite,
      });
      toast.success(
        `Configuração ${data.action === "overwritten" ? "substituída" : "copiada"}: ${data.config?.rules?.length || 0} regra(s)${data.config?.enabled ? " · ATIVO" : ""}`
      );
      setCopyDialogOpen(false);
      fetchAutoConfig();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao copiar configuração");
    } finally {
      setCopying(false);
    }
  };

  const openDiagnostics = async () => {
    if (!integration?.id) return;
    setDiagOpen(true);
    setDiagLoading(true);
    setDiag(null);
    try {
      const params = new URLSearchParams();
      if (selectedCompany) params.append("company_id", selectedCompany);
      params.append("integration_id", integration.id);
      const { data } = await api.get(`/bling/auto-billing/diagnostics?${params.toString()}`);
      setDiag(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao buscar diagnóstico");
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const saveAutoConfig = async (nextConfig) => {
    setAutoSaving(true);
    try {
      const { data } = await api.put("/bling/auto-billing/config", {
        enabled: !!nextConfig.enabled,
        rules: (nextConfig.rules || []).map((r) => ({
          marketplace: r.marketplace,
          enabled: !!r.enabled,
          discount_pct: Number(r.discount_pct || 0),
        })),
        skip_discount_for_cnpj: !!nextConfig.skip_discount_for_cnpj,
        company_id: selectedCompany || null,
        // Persist per-Bling so Matriz vs Filial can have different rules.
        integration_id: integration?.id || null,
      });
      setAutoConfig(data);
      toast.success(data.enabled
        ? "Faturamento automático ATIVADO"
        : "Faturamento automático desativado");
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao salvar");
      throw e;
    } finally {
      setAutoSaving(false);
    }
  };

  const toggleAutoBilling = async () => {
    if (!autoConfig) return;
    // Turning OFF is direct — no confirmation, just save.
    if (autoConfig.enabled) {
      await saveAutoConfig({ ...autoConfig, enabled: false });
      return;
    }
    // Turning ON — need at least one marketplace rule active.
    const anyRuleActive = (autoConfig.rules || []).some(
      (r) => r.enabled && Number(r.discount_pct) >= 0
    );
    if (!anyRuleActive) {
      toast.error("Configure ao menos uma regra de marketplace antes de ativar.");
      setAutoConfigOpen(true);
      return;
    }
    // Show confirmation card so the user picks between START or CONFIGURE
    setAutoStartConfirm(true);
  };

  const confirmStartAuto = async () => {
    setAutoStartConfirm(false);
    // 1) Enable in the backend
    const saved = await saveAutoConfig({ ...autoConfig, enabled: true });
    if (!saved || !saved.enabled) return;
    // 2) Kick off an immediate processing pass — the polling effect will
    //    make the list shrink in real time as pedidos are invoiced.
    await runAutoNow();
  };

  const runAutoNow = async () => {
    setAutoRunning(true);
    try {
      const qp = new URLSearchParams();
      if (selectedCompany) qp.append("company_id", selectedCompany);
      if (integration?.id) qp.append("integration_id", integration.id);
      const params = qp.toString() ? `?${qp.toString()}` : "";
      const { data } = await api.post(`/bling/auto-billing/run-now${params}`);
      const total = (data.discounted || 0) + (data.invoiced || 0);
      const errs = (data.errors || []).length;
      if (data.error) {
        toast.error(`Falha: ${data.error}`);
      } else if (total === 0 && errs === 0) {
        // Show a rich diagnostic — helps the user understand WHY nothing
        // ran: mismatched marketplaces, disabled rules, already-processed…
        const parts = [];
        if (data.fetched > 0) {
          parts.push(`${data.fetched} pedido(s) em aberto no Bling`);
          if (data.missing_rules?.length) {
            const detail = data.missing_rules
              .map((m) => `${m.count} × ${m.marketplace}`).join(", ");
            parts.push(`sem regra: ${detail}`);
          }
          if (data.disabled_rules?.length) {
            const detail = data.disabled_rules
              .map((m) => `${m.count} × ${m.marketplace}`).join(", ");
            parts.push(`regra desativada: ${detail}`);
          }
          if (data.skipped_already_processed > 0) {
            parts.push(`${data.skipped_already_processed} já processado(s)`);
          }
        } else {
          parts.push("Nenhum pedido em aberto no Bling nos últimos 90 dias");
        }
        // Show a persistent toast so the user has time to read it
        toast.warning(parts.join(" · "), {
          duration: 12_000,
          action: {
            label: "Abrir config",
            onClick: () => setAutoConfigOpen(true),
          },
        });
      } else {
        toast.success(
          `Processado: ${data.discounted || 0} desconto(s) + ${data.invoiced || 0} NF-e. ` +
          (errs ? `${errs} erro(s).` : "")
        );
      }
      fetchAutoConfig();
      // Refresh the orders list so faturados desaparecem
      if (fetched) fetchOrders();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao processar");
    } finally {
      setAutoRunning(false);
    }
  };

  // -----------------------------------------------------------------
  // Invoice-failure surface (retry engine + manual attention list)
  // -----------------------------------------------------------------
  const [purgingFailures, setPurgingFailures] = useState(false);

  const fetchFailures = async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setFailuresLoading(true);
    try {
      const qp = new URLSearchParams();
      if (selectedCompany) qp.append("company_id", selectedCompany);
      if (integration?.id) qp.append("integration_id", integration.id);
      const params = qp.toString() ? `?${qp.toString()}` : "";
      const { data } = await api.get(`/bling/invoice-failures${params}`);
      setFailures(data);
      // Surface auto self-heal — user opens modal expecting 5 stuck items
      // and only sees 1, they should know why. Silent polls don't toast
      // (would spam the UI every 45s).
      if (!silent && data?.self_healed > 0) {
        toast.success(
          `${data.self_healed} pedido${data.self_healed > 1 ? "s" : ""} já ` +
          `estava${data.self_healed > 1 ? "m" : ""} autorizado${data.self_healed > 1 ? "s" : ""} no Bling — ` +
          `limpamos automaticamente do painel.`
        );
      }
    } catch (e) {
      if (!silent) console.error("failures fetch:", e);
    } finally {
      if (!silent) setFailuresLoading(false);
    }
  };

  // Poll failures every 45s while the page is open — silent (no loader
  // flicker on the refresh button). Skips when the tab is hidden.
  useEffect(() => {
    if (!integration) return;
    fetchFailures();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchFailures({ silent: true });
      }
    }, 45000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration, selectedCompany]);

  // Live-refresh the orders list while a batch operation is running
  // (auto-billing "Executar agora" OR manual invoicing). This makes
  // the list visually shrink in real time as pedidos get invoiced.
  useEffect(() => {
    if (!integration) return;
    if (!autoRunning && !invoicing) return;
    // Poll every 3s while a job is in progress
    const id = setInterval(() => {
      fetchOrders({ silent: true });
      fetchFailures();
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunning, invoicing, integration]);

  // Silent background refresh — every 60s pull fresh orders from Bling
  // so new marketplace orders show up without the user having to click
  // the refresh button. Skipped when a job is already polling (above)
  // or when the tab is not visible (browser throttles anyway).
  useEffect(() => {
    if (!integration) return;
    if (autoRunning || invoicing) return; // faster polling already active
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchOrders({ silent: true });
      }
    }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration, selectedCompany, autoRunning, invoicing]);

  const retryOneFailure = async (orderId) => {
    setRetryingId(orderId);
    try {
      const params = selectedCompany ? `?company_id=${selectedCompany}` : "";
      const { data } = await api.post(`/bling/invoice-failures/${orderId}/retry${params}`);
      if (data.ok) {
        toast.success(data.already_invoiced ? "Já estava faturado — removido da lista." : "Faturado com sucesso!");
      } else {
        toast.error(`Ainda com erro: ${data.error || "desconhecido"}`);
      }
      fetchFailures();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao retentar");
    } finally {
      setRetryingId(null);
    }
  };

  const dismissFailure = async (orderId) => {
    try {
      const params = selectedCompany ? `?company_id=${selectedCompany}` : "";
      await api.delete(`/bling/invoice-failures/${orderId}${params}`);
      toast.success("Registro descartado.");
      fetchFailures();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao descartar");
    }
  };

  const purgeAllFailures = async ({ force = false } = {}) => {
    setPurgingFailures(true);
    try {
      const qs = new URLSearchParams();
      if (selectedCompany) qs.append("company_id", selectedCompany);
      if (force) qs.append("force", "true");
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const { data } = await api.post(`/bling/invoice-failures/purge-all${suffix}`);
      const parts = [];
      if (data.healed) parts.push(`${data.healed} cura${data.healed > 1 ? "das" : "da"}`);
      if (data.force_deleted) parts.push(`${data.force_deleted} removida${data.force_deleted > 1 ? "s" : ""} à força`);
      if (data.kept) parts.push(`${data.kept} permanece${data.kept > 1 ? "m" : ""}`);
      const msg = parts.length
        ? `${data.total} verificada${data.total > 1 ? "s" : ""} · ${parts.join(" · ")}`
        : "Lista já estava limpa.";
      toast.success(msg);
      fetchFailures();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao limpar lista.");
    } finally {
      setPurgingFailures(false);
    }
  };

  const allRows = useMemo(() => {
    return orders.map((o) => {
      const id = pick(o, "id");
      const numero = pick(o, "numero", "numeroLoja") || id;
      const data = pick(o, "data", "dataSaida");
      const cliente = pick(o, "contato.nome");
      const itens = o.itens || [];
      // "valor" = ORIGINAL total (before ANY discount — Vyntric or
      // marketplace). Bling's LIST endpoint only returns ``total``, which
      // is POST-discount, so we backfill from DETAIL fields when the
      // backend enriched them: original = totalProdutos + frete +
      // outrasDespesas. We also honour ``_original_total`` (snapshot
      // captured before Vyntric mutated the pedido).
      // "current" = the CURRENT total in Bling (equals valor if no
      // discount was applied yet, else the reduced total).
      const currentTotal = Number(pick(o, "total") || 0);
      const totalProdutos = Number(o.totalProdutos ?? 0);
      const freteVal = Number(o.frete ?? 0);
      const outrasVal = Number(o.outrasDespesas ?? 0);
      const originalFromDetail = totalProdutos > 0
        ? (totalProdutos + freteVal + outrasVal)
        : 0;
      const valor = Number(
        o._original_total
        ?? (originalFromDetail > 0 ? originalFromDetail : currentTotal)
      );
      const situacao = o.situacao || null;
      const backendDesc = o.desconto || null;
      const backendPct = backendDesc && backendDesc.unidade === "PERCENTUAL"
        ? Number(backendDesc.valor || 0) : 0;
      const alreadyLocked = o.already_discounted_by_vyntric === true;
      const vyntricPct = Number(o.vyntric_discount_pct || 0);
      const localPct = appliedLocal[id];
      const pctFinal = Number.isFinite(localPct) && localPct > 0
        ? localPct
        : vyntricPct > 0 ? vyntricPct
        : backendPct > 0 ? backendPct : 0;
      // Valor final = Bling's authoritative "total" whenever a discount is
      // recorded on the pedido (either applied now or pre-existing).
      const valor_final = (pctFinal > 0 || currentTotal !== valor)
        ? currentTotal
        : valor;
      // "hasDiscount" = pedido has a discount recorded (visual only).
      // Rows stay SELECTABLE regardless of discount state, so the user
      // can select pedidos that were just discounted to send them to
      // faturamento in the same session. Backend enforces idempotency
      // on the discount action, so re-selecting is safe.
      const hasDiscount = alreadyLocked || Number.isFinite(localPct);
      return {
        id, numero, numeroLoja: pick(o, "numeroLoja") || "",
        marketplace: o._marketplace || "Desconhecido",
        data, cliente, itens, valor, valor_final,
        pct: pctFinal, situacao,
        hasDiscount,
        lockedAt: o.vyntric_discount_at,
        raw: o,
      };
    });
  }, [orders, appliedLocal]);

  // Build list of marketplaces present in the current orders + counts
  const marketplaceTabs = useMemo(() => {
    const counts = new Map();
    for (const r of allRows) {
      counts.set(r.marketplace, (counts.get(r.marketplace) || 0) + 1);
    }
    const items = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    return items;
  }, [allRows]);

  const rows = useMemo(() => {
    let base = allRows;
    if (activeMarketplace !== "__all__") {
      base = base.filter((r) => r.marketplace === activeMarketplace);
    }
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) =>
      String(r.numero || "").toLowerCase().includes(q)
      || String(r.numeroLoja || "").toLowerCase().includes(q)
      || String(r.id || "").toLowerCase().includes(q)
      || String(r.cliente || "").toLowerCase().includes(q)
    );
  }, [allRows, searchQuery, activeMarketplace]);

  const selectableRows = useMemo(() => rows.filter((r) => r.id != null), [rows]);
  // How many of the currently-selected pedidos are eligible for a NEW
  // discount (i.e. no discount recorded yet by Vyntric on that order_id).
  const eligibleForDiscountCount = useMemo(() => {
    const alreadyIds = new Set(allRows.filter((r) => r.hasDiscount).map((r) => r.id));
    let n = 0;
    selected.forEach((id) => { if (!alreadyIds.has(id)) n += 1; });
    return n;
  }, [selected, allRows]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableRows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    const row = rows.find((r) => r.id === id);
    if (row?.locked) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (bootLoading) {
    return (
      <div className="py-24 flex justify-center" data-testid="bling-discount-page">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="space-y-4" data-testid="bling-discount-page">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Bling ERP</div>
          <h1 className="text-3xl font-black tracking-tighter">Desconto no Bling ERP</h1>
        </div>
        <div
          className="border border-amber-300 bg-amber-50 rounded-xl p-6 flex gap-4 items-start"
          data-testid="bling-not-connected"
        >
          <AlertTriangle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="space-y-2 text-sm text-amber-900">
            <div className="font-bold text-base">Bling não conectado.</div>
            <p>
              Este módulo usa a <span className="font-mono font-bold">API v3 (OAuth2)</span> do Bling.
              Cadastre o App em{" "}
              <a
                href="https://developer.bling.com.br"
                target="_blank"
                rel="noreferrer"
                className="underline font-bold hover:text-amber-950"
              >
                developer.bling.com.br
              </a>
              , configure o Client ID e Secret em{" "}
              <Link to="/platform-settings" className="underline font-bold hover:text-amber-950">
                Credenciais de Plataforma
              </Link>{" "}
              e autorize a conta na{" "}
              <Link to="/integrations" className="underline font-bold hover:text-amber-950">
                Central de Integrações
              </Link>.
            </p>
            <p className="font-mono text-xs bg-amber-100 px-2 py-1 inline-block rounded">
              Redirect URI: <b>https://vyntric.com.br/api/oauth/callback</b>
            </p>
            <div className="flex gap-2 pt-2">
              <Link
                to="/platform-settings"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-slate-900 text-white hover:bg-[#002EA6]"
              >
                <KeyRound className="w-3.5 h-3.5" /> Credenciais
              </Link>
              <Link
                to="/integrations"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-amber-700 text-amber-900 hover:bg-amber-100"
              >
                <Plug className="w-3.5 h-3.5" /> Integrações
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bling-discount-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Bling ERP</div>
          <h1 className="text-3xl font-black tracking-tighter">Desconto no Bling ERP</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Aplique desconto em percentual (PERCENTUAL) diretamente em pedidos de venda do Bling.
            O sistema traz apenas pedidos <b>Em Aberto</b> — únicos elegíveis para emissão de NF.
            O valor entra no campo <span className="font-mono font-bold">desconto</span> do pedido e
            reduz o total automaticamente.
          </p>
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-800"
          data-testid="bling-status-badge"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Bling conectado
          {(() => {
            // Prefer nickname (user-visible name for THIS Bling) so
            // multi-Bling tenants see "Loja Matriz" / "Filial SP" here,
            // not the same "LUMA" company name for both.
            const companyName = companies.find((c) => c.id === integration.company_id)?.nome_fantasia;
            const label = blingLabel(integration) || companyName || integration.account_label;
            return label ? (
              <span className="font-mono normal-case font-normal text-emerald-700">
                · {label}
              </span>
            ) : null;
          })()}
        </div>
      </div>

      {blings.length > 1 && (
        <div
          className="bg-white border-2 border-slate-200 rounded-xl p-2"
          data-testid="bling-tenant-tabs"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2">
              Empresa / Bling:
            </div>
            {blings.map((b) => {
              // Prefer the user-set nickname (ex.: "Luma", "Aeromix").
              // Falls back to the API-provided name, then the company's
              // razão social, then a short id — in that order.
              const companyName = companies.find((c) => c.id === b.company_id)?.nome_fantasia;
              const label = blingLabel(b) || companyName || b.account_label || `Bling #${b.id.slice(0, 6)}`;
              const isActive = b.id === (integration?.id || "");
              return (
                <button
                  key={b.id}
                  data-testid={`bling-tab-${b.id}`}
                  onClick={() => setSelectedBlingId(b.id)}
                  className={`px-4 py-2 text-xs font-black uppercase tracking-wider border-2 rounded-sm transition-all inline-flex items-center gap-2 ${
                    isActive
                      ? "border-[#002EA6] bg-[#002EA6] text-white shadow-md"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  <Plug className={`w-3 h-3 ${isActive ? "" : "text-slate-400"}`} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Banner de "ação manual" removido a pedido do usuário — as falhas
          continuam disponíveis via botão "Ver falhas" no painel abaixo,
          quando necessário. Preservamos o estado ``failures`` para não
          quebrar o restante da UI (contador embutido no card). */}

      <div
        className="border border-slate-200 bg-slate-50/60 rounded-xl p-4 space-y-3"
        data-testid="bling-control-panel"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <button
            data-testid="bling-fetch-orders"
            onClick={fetchOrders}
            disabled={loading}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: "#002EA6" }}
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : fetched ? <RefreshCw className="w-3.5 h-3.5" />
              : <Search className="w-3.5 h-3.5" />}
            {fetched ? "Atualizar pedidos" : "Buscar pedidos"}
          </button>
          <button
            data-testid="bling-open-history"
            onClick={openHistory}
            className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 flex items-center justify-center gap-2"
          >
            <History className="w-3.5 h-3.5" />
            Histórico
          </button>
          {user?.role === "admin" && fetched && (
            <button
              data-testid="bling-reset-tracking"
              onClick={resetTracking}
              disabled={resetting || loading}
              className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider border border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
              Resetar tracking
            </button>
          )}
        </div>

        {/* Auto-billing bar */}
        {autoConfig && (
          <div
            className="flex items-center justify-between flex-wrap gap-3 border-t border-slate-200 pt-3"
            data-testid="bling-auto-billing-bar"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <button
                data-testid="bling-auto-toggle"
                onClick={toggleAutoBilling}
                disabled={autoSaving}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-black uppercase tracking-wider border-2 disabled:opacity-60 transition-all ${
                  autoConfig.enabled
                    ? "bg-emerald-600 text-white border-emerald-700 shadow-md hover:bg-emerald-700"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {autoConfig.enabled ? (
                  <Loader2 className="w-4 h-4 animate-spin" data-testid="bling-auto-active-spinner" />
                ) : (
                  <Zap className="w-4 h-4 text-slate-500" />
                )}
                Faturamento Automático
                <span
                  className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black rounded-full ${
                    autoConfig.enabled
                      ? "bg-white/25 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {autoConfig.enabled ? (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                      ATIVO
                    </>
                  ) : "OFF"}
                </span>
              </button>

              <button
                data-testid="bling-auto-configure"
                onClick={() => setAutoConfigOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
              >
                <Settings className="w-3.5 h-3.5" />
                Configurar regras
              </button>

              <button
                data-testid="bling-store-mappings-open"
                onClick={() => setStoreMappingsOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
                title="Cadastrar nomes de loja do Bling e associar a um marketplace"
              >
                <Store className="w-3.5 h-3.5" />
                Mapeamento de Lojas
              </button>

              {blings.length > 1 && (
                <button
                  data-testid="bling-auto-copy-config"
                  onClick={openCopyDialog}
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
                  title="Copiar as regras de auto-faturamento de outro Bling desta conta"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  Copiar de outro Bling
                </button>
              )}

              <button
                data-testid="bling-auto-diagnostics"
                onClick={openDiagnostics}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-800"
                title="Ver por que o robô pulou pedidos na última rodada"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Diagnóstico
              </button>
            </div>

            <div className="text-[11px] font-mono text-slate-500 text-right">
              {autoConfig.enabled ? (
                <>
                  {autoRunning ? (
                    <span className="text-emerald-700 font-bold uppercase tracking-wider">
                      Processando pedidos agora…
                    </span>
                  ) : (
                    <>
                      <AutoBillingCountdown
                        nextRunAt={autoConfig?.scheduler?.next_run_at}
                        running={!!autoConfig?.scheduler?.running}
                        onDue={fetchAutoConfig}
                      />
                      {autoConfig.last_run_stats && (
                        <div className="text-slate-400 mt-0.5">
                          Último: {autoConfig.last_run_stats.discounted || 0} desc / {autoConfig.last_run_stats.invoiced || 0} NF
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>Ative para descontar + faturar automaticamente novos pedidos</>
              )}
            </div>
          </div>
        )}

        {autoStartConfirm && autoConfig && (
          <StartConfirmCard
            config={autoConfig}
            onConfirm={confirmStartAuto}
            onConfigure={() => {
              setAutoStartConfirm(false);
              setAutoConfigOpen(true);
            }}
            onCancel={() => setAutoStartConfirm(false)}
          />
        )}

        {fetched && orders.length > 0 && (
          <div className="pt-3 border-t border-slate-200 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-xs text-slate-600 flex items-center gap-2" data-testid="bling-filter-info">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: "#F59E0B" }}
                />
                Exibindo apenas pedidos <b>Em Aberto</b> (elegíveis para emissão de NF)
                <span className="text-slate-400 font-mono">· {orders.length} pedido(s)</span>
                {(autoRunning || invoicing) && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded-sm animate-pulse"
                    data-testid="bling-live-updating-badge"
                  >
                    <Loader2 className="w-2.5 h-2.5 animate-spin" />
                    Atualizando ao vivo
                  </span>
                )}
              </div>
              <div className="ml-auto relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <input
                  data-testid="bling-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nº pedido, loja ou cliente"
                  className="border border-slate-300 bg-white pl-8 pr-3 py-1.5 text-xs rounded-sm w-72 focus:outline-none focus:border-[#002EA6]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
                  Percentual de desconto
                </span>
                <div className="relative">
                  <input
                    data-testid="bling-discount-pct"
                    type="number"
                    min="0.1"
                    max="100"
                    step="0.1"
                    value={discountPct}
                    onChange={(e) => setDiscountPct(e.target.value)}
                    placeholder="Ex: 10"
                    className="w-full border border-slate-300 bg-white pl-3 pr-8 py-2 text-sm rounded-sm font-mono focus:outline-none focus:border-[#002EA6]"
                  />
                  <span className="absolute right-3 top-2.5 text-slate-500 text-sm font-mono">%</span>
                </div>
              </label>
              <div className="text-xs text-slate-600" data-testid="bling-preview">
                {validPct && selected.size > 0 ? (
                  <>
                    <span className="font-bold text-slate-900">{eligibleForDiscountCount}</span> elegível(is)
                    {selected.size !== eligibleForDiscountCount && (
                      <span className="text-slate-400"> ({selected.size - eligibleForDiscountCount} já descontado)</span>
                    )} · <span className="font-bold text-slate-900">{pctNum}%</span>
                  </>
                ) : (
                  <span className="text-slate-400">
                    Selecione pedidos e informe um percentual válido.
                  </span>
                )}
              </div>
              <button
                data-testid="bling-apply-discount"
                onClick={applyDiscount}
                disabled={applying || invoicing || !validPct || eligibleForDiscountCount === 0 || autoConfig?.enabled}
                title={autoConfig?.enabled ? "Desligue o Faturamento Automático para operar manualmente" : undefined}
                className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#002EA6" }}
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgePercent className="w-3.5 h-3.5" />}
                Aplicar em {eligibleForDiscountCount} pedido(s)
              </button>
            </div>

            {autoConfig?.enabled && (
              <div
                className="border border-emerald-300 bg-emerald-50 rounded-md px-4 py-3 flex items-center gap-3"
                data-testid="bling-auto-lock-notice"
              >
                <Zap className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                <div className="text-xs text-emerald-900">
                  <b>Modo automático ATIVO.</b> Ações manuais (aplicar desconto e faturar) estão bloqueadas
                  para evitar conflitos com o ciclo automático. Desligue o
                  <b> Faturamento Automático</b> acima para operar manualmente.
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-slate-600">
                <span className="font-bold text-slate-900">Faturamento:</span>{" "}
                emite NF-e no Bling e envia à SEFAZ.
                O Bling sincroniza a NF com o marketplace de origem automaticamente.
              </div>
              <button
                data-testid="bling-invoice-selected"
                onClick={invoiceSelected}
                disabled={invoicing || applying || selected.size === 0 || autoConfig?.enabled}
                title={autoConfig?.enabled ? "Desligue o Faturamento Automático para operar manualmente" : undefined}
                className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#059669" }}
              >
                {invoicing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                Faturar {selected.size} pedido(s)
              </button>
            </div>
          </div>
        )}
      </div>

      {warning && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-xl p-4 flex gap-3 items-start"
          data-testid="bling-warning"
        >
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">{warning}</div>
        </div>
      )}

      {results && (
        <div
          className={`border rounded-xl p-4 ${
            (results.failed?.length || 0) === 0
              ? "border-emerald-200 bg-emerald-50/60"
              : "border-amber-200 bg-amber-50/70"
          }`}
          data-testid="bling-results-panel"
        >
          <div className="flex items-start gap-3">
            {(results.failed?.length || 0) === 0 ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="text-sm font-bold">
                {results.ok?.length || 0} sucesso(s) · {results.failed?.length || 0} falha(s)
              </div>
              {(results.failed?.length || 0) > 0 && (
                <ul className="mt-2 text-xs space-y-1 font-mono text-amber-900" data-testid="bling-results-failed">
                  {results.failed.map((f, i) => (
                    <li key={i}>
                      <span className="font-bold">#{f.numero || f.id}</span> — {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {fetched && marketplaceTabs.length > 0 && (
        <div className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto" data-testid="bling-marketplace-tabs">
          <button
            data-testid="bling-tab-all"
            onClick={() => { setActiveMarketplace("__all__"); setSelected(new Set()); }}
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              activeMarketplace === "__all__"
                ? "border-[#002EA6] text-[#002EA6]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            Todos <span className="ml-1 font-mono text-slate-400">({allRows.length})</span>
          </button>
          {marketplaceTabs.map((tab) => (
            <button
              key={tab.name}
              data-testid={`bling-tab-${tab.name.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => { setActiveMarketplace(tab.name); setSelected(new Set()); }}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                activeMarketplace === tab.name
                  ? "border-[#002EA6] text-[#002EA6]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {tab.name} <span className="ml-1 font-mono text-slate-400">({tab.count})</span>
            </button>
          ))}
        </div>
      )}

      {fetched && (
        <div className="border border-slate-200 bg-white overflow-hidden">
          {rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="bling-empty">
              Nenhum pedido <b>Em Aberto</b> encontrado no Bling nos últimos 90 dias.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-base">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-600">
                    <tr>
                      <th className="px-3 py-3 w-8 text-center">
                        <input
                          data-testid="bling-check-all"
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          className="w-4 h-4 cursor-pointer"
                          style={{ accentColor: "#002EA6" }}
                        />
                      </th>
                      <th className="text-left px-3 py-3">Nº Pedido</th>
                      <th className="text-left px-3 py-3">Data</th>
                      <th className="text-left px-3 py-3">Cliente</th>
                      <th className="text-right px-3 py-3">Itens</th>
                      <th className="text-right px-3 py-3">Valor original</th>
                      <th className="text-right px-3 py-3">Desconto</th>
                      <th className="text-right px-3 py-3">Valor final</th>
                      <th className="text-left px-3 py-3">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isSel = selected.has(r.id);
                      const meta = situacaoMeta(r.situacao);
                      const saved = r.valor - r.valor_final;
                      const hasDiscount = r.hasDiscount;
                      return (
                        <tr
                          key={r.id ?? r.numero}
                          data-testid={`bling-order-${r.numero}`}
                          onClick={() => r.id != null && toggleOne(r.id)}
                          className={`border-b border-slate-100 transition-colors ${
                            isSel ? "bg-blue-50 ring-1 ring-inset ring-[#002EA6] cursor-pointer"
                              : hasDiscount ? "bg-emerald-50/30 hover:bg-emerald-50/60 cursor-pointer"
                              : "hover:bg-slate-50 cursor-pointer"
                          }`}
                          title={hasDiscount ? `Desconto já aplicado por Vyntric${r.lockedAt ? ` em ${String(r.lockedAt).slice(0,16).replace('T',' ')}` : ''} — pode selecionar para faturar` : undefined}
                        >
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              data-testid={`bling-check-${r.numero}`}
                              type="checkbox"
                              checked={isSel}
                              onChange={() => r.id != null && toggleOne(r.id)}
                              disabled={r.id == null}
                              className="w-4 h-4 cursor-pointer"
                              style={{ accentColor: "#002EA6" }}
                            />
                          </td>
                          <td className="px-3 py-3 font-mono text-sm font-bold">
                            <div>#{r.numero}</div>
                            {r.numeroLoja && r.numeroLoja !== String(r.numero) && (
                              <div className="text-[10px] text-slate-500 font-normal truncate max-w-[140px]" title={r.numeroLoja}>
                                {r.numeroLoja}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3 text-sm text-slate-700">{fmtDate(r.data)}</td>
                          <td className="px-3 py-3 text-sm text-slate-800">
                            {typeof r.cliente === "string" ? r.cliente : (r.cliente?.nome || "—")}
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-sm text-slate-600">
                            {(r.itens || []).length}
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-sm text-slate-600">
                            {brl(r.valor)}
                          </td>
                          <td className="px-3 py-3 text-right">
                            {r.pct > 0 ? (
                              <div className="flex flex-col items-end">
                                <span className="text-sm font-mono font-bold text-emerald-700">
                                {r.pct}%
                                </span>
                                {hasDiscount && (
                                  <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">
                                    Aplicado
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <div className="font-mono text-base font-bold">{brl(r.valor_final)}</div>
                            {r.pct > 0 && (
                              <div className="text-[11px] font-mono text-emerald-700">
                                economia {brl(saved)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className="inline-block px-2 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm"
                              style={{ background: meta.bg, color: meta.fg }}
                            >
                              {meta.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2.5 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between text-xs">
                <div className="text-slate-600">
                  <span className="font-bold text-slate-900">{rows.length}</span> pedido(s) ·{" "}
                  <span className="font-bold text-slate-900">{selected.size}</span> selecionado(s)
                </div>
                <button
                  data-testid="bling-clear-selection"
                  onClick={() => setSelected(new Set())}
                  disabled={selected.size === 0}
                  className="text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900 disabled:opacity-40"
                >
                  Limpar seleção
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {historyOpen && (
        <HistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          items={historyItems}
          loading={historyLoading}
          dateFrom={historyDateFrom}
          dateTo={historyDateTo}
          setDateFrom={setHistoryDateFrom}
          setDateTo={setHistoryDateTo}
          query={historyQuery}
          setQuery={setHistoryQuery}
          onFilter={fetchHistory}
        />
      )}

      {autoConfigOpen && autoConfig && (
        <AutoConfigDialog
          open={autoConfigOpen}
          onClose={() => setAutoConfigOpen(false)}
          config={autoConfig}
          saving={autoSaving}
          onSave={async (nextConfig) => {
            await saveAutoConfig(nextConfig);
            setAutoConfigOpen(false);
          }}
        />
      )}

      {storeMappingsOpen && integration && (
        <StoreMappingsDialog
          onClose={() => setStoreMappingsOpen(false)}
          integration={integration}
        />
      )}

      {failuresOpen && (
        <FailuresDialog
          open={failuresOpen}
          onClose={() => setFailuresOpen(false)}
          data={failures}
          loading={failuresLoading}
          onRefresh={() => fetchFailures()}
          onRetry={retryOneFailure}
          onDismiss={dismissFailure}
          onPurgeAll={purgeAllFailures}
          purging={purgingFailures}
          retryingId={retryingId}
        />
      )}

      {copyDialogOpen && (
        <CopyConfigDialog
          onClose={() => setCopyDialogOpen(false)}
          blings={blings}
          currentBling={integration}
          companies={companies}
          sourceId={copySourceId}
          setSourceId={setCopySourceId}
          overwrite={copyOverwrite}
          setOverwrite={setCopyOverwrite}
          onConfirm={runCopyConfig}
          loading={copying}
          hasExistingConfig={!!autoConfig?.rules?.length}
        />
      )}

      {diagOpen && (
        <DiagnosticsDialog
          onClose={() => setDiagOpen(false)}
          loading={diagLoading}
          data={diag}
          integration={integration}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Auto-billing countdown timer — replaces the static "a cada 2 min" copy.
// Ticks every 500ms and shows the exact seconds until the next scheduled
// APScheduler pass. Calls `onDue` once when the countdown expires so the
// parent can re-fetch the config (which returns the fresh `next_run_at`
// and the last-run stats).
// ---------------------------------------------------------------------------
function AutoBillingCountdown({ nextRunAt, running, onDue }) {
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [nextRunAt]);

  if (!running) {
    return (
      <span className="text-red-700 font-bold uppercase tracking-wider" data-testid="auto-billing-countdown-off">
        Scheduler parado — reinicie o backend
      </span>
    );
  }
  if (!nextRunAt) {
    return (
      <span className="text-amber-700 font-bold uppercase tracking-wider" data-testid="auto-billing-countdown-unknown">
        Ciclo automático: <b className="text-slate-700">a cada 2 min · 24h/dia</b>
      </span>
    );
  }
  const remainingMs = new Date(nextRunAt).getTime() - now;
  if (remainingMs <= 0) {
    // Fire onDue once, then keep showing "processando…" until the parent
    // refreshes and gives us a new next_run_at.
    if (!firedRef.current && typeof onDue === "function") {
      firedRef.current = true;
      // Give the scheduler ~2s head-start before refetching so we grab
      // the FRESH next_run_at, not the same one that just fired.
      setTimeout(() => onDue(), 2000);
    }
    return (
      <span className="text-emerald-700 font-bold uppercase tracking-wider animate-pulse" data-testid="auto-billing-countdown-firing">
        Executando ciclo agora…
      </span>
    );
  }
  const totalSec = Math.floor(remainingMs / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  const pretty = mm > 0 ? `${mm}m ${String(ss).padStart(2, "0")}s` : `${ss}s`;
  return (
    <span data-testid="auto-billing-countdown" className="inline-flex items-center gap-1.5">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
      </span>
      <span>
        Próximo ciclo em <b className="text-slate-800 font-mono" data-testid="auto-billing-countdown-value">{pretty}</b>
      </span>
    </span>
  );
}


// ---------------------------------------------------------------------------
// Invoice History Dialog — 90-day retention (TTL enforced by MongoDB)
// ---------------------------------------------------------------------------
function HistoryDialog({
  open, onClose, items, loading,
  dateFrom, dateTo, setDateFrom, setDateTo,
  query, setQuery, onFilter,
}) {
  if (!open) return null;
  const totalValue = items.reduce((acc, it) => acc + Number(it.valor_total || 0), 0);
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bling-history-backdrop"
    >
      <div
        className="bg-white w-full max-w-6xl max-h-[90vh] flex flex-col rounded-md shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="bling-history-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-slate-700" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-900">
              Histórico de Pedidos Faturados
            </h2>
            <span className="text-[10px] font-mono text-slate-500 ml-2">
              retenção: 90 dias
            </span>
          </div>
          <button
            data-testid="bling-history-close"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60">
          <div className="grid grid-cols-1 md:grid-cols-[auto_auto_1fr_auto] gap-3 items-end">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
                Data inicial
              </span>
              <input
                data-testid="bling-history-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border border-slate-300 bg-white px-2 py-2 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
                Data final
              </span>
              <input
                data-testid="bling-history-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border border-slate-300 bg-white px-2 py-2 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">
                Buscar
              </span>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <input
                  data-testid="bling-history-query"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onFilter()}
                  placeholder="Nº pedido, cliente, marketplace, NF, e-mail"
                  className="w-full border border-slate-300 bg-white pl-8 pr-2 py-2 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
                />
              </div>
            </label>
            <button
              data-testid="bling-history-filter"
              onClick={onFilter}
              disabled={loading}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white flex items-center justify-center gap-2 disabled:opacity-60 rounded-sm"
              style={{ background: "#002EA6" }}
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              Filtrar
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando histórico...
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Nenhum pedido faturado no período selecionado.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-slate-200 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Nº Pedido</th>
                  <th className="px-3 py-2 text-left">Marketplace</th>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-right">Desc %</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-left">NF-e ID</th>
                  <th className="px-3 py-2 text-center">SEFAZ</th>
                  <th className="px-3 py-2 text-left">Emitido por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="bling-history-tbody">
                {items.map((it, idx) => {
                  const dt = it.invoiced_at_dt || it.invoiced_at || "";
                  const d = dt ? new Date(dt) : null;
                  const label = d && !isNaN(d.getTime())
                    ? d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                    : "—";
                  return (
                    <tr key={`${it.order_id}-${idx}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs font-mono text-slate-600">{label}</td>
                      <td className="px-3 py-2 font-mono font-bold text-slate-900">{it.numero || it.order_id}</td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-slate-100 text-slate-700">
                          {it.marketplace || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700 max-w-[200px] truncate">{it.cliente_nome || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {it.discount_pct ? `${Number(it.discount_pct).toFixed(2).replace(".", ",")}%` : "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm font-bold">
                        {brl(Number(it.valor_total || 0))}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{it.nfe_id || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        {it.sent_to_sefaz ? (
                          <span className="text-emerald-700 text-xs font-bold">✓ Autorizada</span>
                        ) : (
                          <span className="text-amber-700 text-xs font-bold">Pendente</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-slate-500">{it.invoiced_by_email || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between text-xs">
          <div className="text-slate-600">
            <span className="font-bold text-slate-900">{items.length}</span> pedido(s) ·{" "}
            <span className="font-bold text-slate-900">{brl(totalValue)}</span> em faturamento
          </div>
          <div className="text-[10px] font-mono text-slate-500">
            Registros mais antigos que 90 dias são excluídos automaticamente
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-Billing Configuration Dialog — per-marketplace discount + toggle
// ---------------------------------------------------------------------------
function AutoConfigDialog({ open, onClose, config, saving, onSave }) {
  // Always render EXACTLY the 9 canonical marketplaces in canonical order,
  // preserving any pct/enabled the tenant has saved for each. Legacy
  // rules for marketplaces no longer in the canonical list (e.g., old
  // "Americanas", "Netshoes", "TikTok Shop" with space) are silently
  // dropped from the UI — the store-mapping dialog is the authoritative
  // classifier now, so extra rows would just confuse the user.
  const [draft, setDraft] = useState(() => {
    const existing = new Map();
    for (const r of config.rules || []) {
      if (r && typeof r.marketplace === "string") existing.set(r.marketplace, r);
    }
    return {
      enabled: !!config.enabled,
      skip_discount_for_cnpj: !!config.skip_discount_for_cnpj,
      rules: CANONICAL_MARKETPLACES.map((name) => ({
        marketplace: name,
        enabled: !!existing.get(name)?.enabled,
        discount_pct: Number(existing.get(name)?.discount_pct ?? 0),
      })),
    };
  });

  if (!open) return null;

  const updateRule = (idx, patch) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const activeCount = draft.rules.filter((r) => r.enabled).length;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bling-auto-config-backdrop"
    >
      <div
        className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col rounded-md shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="bling-auto-config-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-600" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-900">
              Configurar Faturamento Automático
            </h2>
          </div>
          <button
            data-testid="bling-auto-config-close"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/60">
          <p className="text-sm text-slate-700">
            Defina o percentual de desconto para cada marketplace. Quando ativado,
            o sistema busca automaticamente novos pedidos <b>Em Aberto</b>,
            aplica o desconto configurado e emite a NF-e — <b>a cada 2 minutos, 24h/dia</b>,
            mesmo sem você estar logado no sistema.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Marketplaces desativados aqui são <b>ignorados</b> — o sistema não
            aplica desconto nem fatura pedidos dessas plataformas.
          </p>
        </div>

        {/* Global rule — skip discount for CNPJ (Pessoa Jurídica) clients */}
        <div className="px-5 py-3 border-b border-slate-200 bg-white">
          <label
            className="flex items-start gap-3 cursor-pointer group"
            data-testid="bling-auto-cnpj-toggle-label"
          >
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                data-testid="bling-auto-skip-cnpj-toggle"
                type="checkbox"
                checked={!!draft.skip_discount_for_cnpj}
                onChange={(e) => setDraft((prev) => ({ ...prev, skip_discount_for_cnpj: e.target.checked }))}
                className="peer sr-only"
              />
              <div className="w-10 h-5 bg-slate-300 rounded-full peer-checked:bg-emerald-600 transition-colors" />
              <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-slate-900">
                Não aplicar desconto em clientes CNPJ (PJ)
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Quando <b>ativado</b>, pedidos de <b>Pessoa Jurídica</b> (CNPJ) são
                faturados sem desconto. O sistema identifica pelo cadastro do cliente no Bling
                (<span className="font-mono text-[10px]">tipoPessoa=J</span> ou CNPJ com 14 dígitos).
                Pedidos de CPF continuam recebendo o desconto configurado por marketplace.
              </div>
            </div>
          </label>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
                <th className="px-2 py-2 text-left w-16">Ativa</th>
                <th className="px-2 py-2 text-left">Marketplace</th>
                <th className="px-2 py-2 text-right w-32">Desconto %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {draft.rules.map((r, idx) => (
                <tr key={r.marketplace} className={r.enabled ? "" : "opacity-50"}>
                  <td className="px-2 py-3">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        data-testid={`bling-auto-rule-enabled-${r.marketplace.toLowerCase().replace(/\s+/g, '-')}`}
                        type="checkbox"
                        checked={!!r.enabled}
                        onChange={(e) => updateRule(idx, { enabled: e.target.checked })}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                      />
                    </label>
                  </td>
                  <td className="px-2 py-3">
                    <span className="text-sm font-bold text-slate-800">{r.marketplace}</span>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        data-testid={`bling-auto-rule-pct-${r.marketplace.toLowerCase().replace(/\s+/g, '-')}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={r.discount_pct}
                        onChange={(e) => updateRule(idx, { discount_pct: Number(e.target.value) })}
                        disabled={!r.enabled}
                        className="w-24 border border-slate-300 bg-white px-2 py-1.5 text-sm font-mono text-right rounded-sm focus:outline-none focus:border-[#002EA6] disabled:bg-slate-100"
                      />
                      <span className="text-sm text-slate-500 font-bold">%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-600">
            <b className="text-slate-900">{activeCount}</b> marketplace(s) ativo(s)
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="bling-auto-config-cancel"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 rounded-sm disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              data-testid="bling-auto-config-save"
              onClick={() => onSave(draft)}
              disabled={saving}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white flex items-center gap-2 disabled:opacity-60 rounded-sm"
              style={{ background: "#002EA6" }}
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Salvar regras
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Store Mappings Dialog — standalone (Iter63).
// Extracted from AutoConfigDialog so the user can manage store→marketplace
// mappings independently from the discount rules. Own state, own loader,
// own footer. Uses the same 9-canonical marketplace list.
// ---------------------------------------------------------------------------
function StoreMappingsDialog({ onClose, integration }) {
  const [mappings, setMappings] = useState([]);
  const [detectedStores, setDetectedStores] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [newStoreName, setNewStoreName] = useState("");
  const [newStoreId, setNewStoreId] = useState("");
  const [newMarketplace, setNewMarketplace] = useState(CANONICAL_MARKETPLACES[0]);
  const [savingMapping, setSavingMapping] = useState(false);

  const load = async () => {
    if (!integration?.id) return;
    setMappingsLoading(true);
    try {
      const [mRes, dRes] = await Promise.all([
        api.get(`/bling/store-mappings?integration_id=${integration.id}`),
        api
          .get(`/bling/store-mappings/detected-stores?integration_id=${integration.id}`)
          .catch(() => ({ data: { items: [] } })),
      ]);
      setMappings(mRes.data?.items || []);
      setDetectedStores(dRes.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar mapeamentos");
    } finally {
      setMappingsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration?.id]);

  const addMapping = async () => {
    const sn = newStoreName.trim();
    const sid = newStoreId.trim();
    if (!sn && !sid) {
      toast.error("Informe o nome exato da loja OU o ID da loja (Bling API).");
      return;
    }
    setSavingMapping(true);
    try {
      await api.post(
        `/bling/store-mappings?integration_id=${integration.id}`,
        {
          store_name: sn || null,
          store_id: sid || null,
          marketplace: newMarketplace,
        },
      );
      toast.success(`Mapeamento salvo como ${newMarketplace}`);
      setNewStoreName("");
      setNewStoreId("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao salvar mapeamento");
    } finally {
      setSavingMapping(false);
    }
  };

  const deleteMapping = async (id, storeName) => {
    if (!window.confirm(`Remover mapeamento de "${storeName}"?`)) return;
    try {
      await api.delete(`/bling/store-mappings/${id}`);
      setMappings((prev) => prev.filter((m) => m.id !== id));
      toast.success("Mapeamento removido.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao remover");
    }
  };

  const mappedLower = new Set(
    mappings.map((m) => (m.store_name || "").trim().toLowerCase()),
  );
  const unmappedDetected = detectedStores.filter(
    (s) => !mappedLower.has((s.store_name || "").trim().toLowerCase()),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
      data-testid="bling-store-mappings-dialog"
    >
      <div
        className="bg-white border-2 border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Faturamento Automático
            </div>
            <div className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Store className="w-5 h-5 text-[#002EA6]" />
              Mapeamento de Lojas
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-xs text-slate-600 leading-relaxed">
            Cadastre o nome <b>exato</b> de cada loja como aparece no Bling
            (ex: <span className="font-mono bg-slate-100 px-1">Shopee 01</span>) e
            associe a um marketplace. O robô usa <b>somente essa lista</b> para
            classificar os pedidos — sem cadastro, tudo cai em &quot;Desconhecido&quot;.
          </p>

          {/* Detected but unmapped stores — quick-cadastra shortcut */}
          {unmappedDetected.length > 0 && (
            <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-900 mb-2">
                Lojas detectadas sem mapeamento ({unmappedDetected.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {unmappedDetected.slice(0, 20).map((s) => (
                  <button
                    key={s.store_name}
                    data-testid="bling-store-quick-pick"
                    onClick={() => setNewStoreName(s.store_name)}
                    className="text-xs bg-white border border-amber-400 hover:bg-amber-100 px-2 py-1 rounded-sm font-mono text-amber-900"
                    title="Clique para preencher o formulário"
                  >
                    {s.store_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add-new form */}
          <div className="p-3 border border-slate-200 bg-slate-50/40 rounded-sm space-y-2">
            <p className="text-[11px] text-slate-500">
              Preencha o <b>nome</b> exatamente como no Bling (ex:{" "}
              <span className="font-mono bg-white px-1">Shopee 01</span>){" "}
              <b>OU/E</b> o <b>ID da loja</b> (Bling → Preferências →
              Integrações → <i>Código da loja API Bling</i>, ex:{" "}
              <span className="font-mono bg-white px-1">206151017</span>). O ID
              é usado como fallback quando o pedido chega sem o nome.
            </p>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  Nome da loja no Bling
                </label>
                <input
                  data-testid="bling-store-mapping-name-input"
                  type="text"
                  value={newStoreName}
                  onChange={(e) => setNewStoreName(e.target.value)}
                  placeholder="Shopee 01"
                  className="w-full border border-slate-300 bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
                />
              </div>
              <div className="w-40">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  ID da loja
                </label>
                <input
                  data-testid="bling-store-mapping-id-input"
                  type="text"
                  value={newStoreId}
                  onChange={(e) => setNewStoreId(e.target.value)}
                  placeholder="206151017"
                  className="w-full border border-slate-300 bg-white px-3 py-2 text-sm rounded-sm font-mono focus:outline-none focus:border-[#002EA6]"
                />
              </div>
              <div className="w-40">
                <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                  Marketplace
                </label>
                <select
                  data-testid="bling-store-mapping-marketplace-select"
                  value={newMarketplace}
                  onChange={(e) => setNewMarketplace(e.target.value)}
                  className="w-full border border-slate-300 bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[#002EA6]"
                >
                  {CANONICAL_MARKETPLACES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <button
                data-testid="bling-store-mapping-add"
                onClick={addMapping}
                disabled={savingMapping || (!newStoreName.trim() && !newStoreId.trim())}
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm disabled:opacity-50 inline-flex items-center gap-1"
              >
                {savingMapping ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Adicionar
              </button>
            </div>
          </div>

          {/* Existing mappings */}
          {mappingsLoading ? (
            <div className="text-center py-6 text-slate-500 text-xs">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
              Carregando mapeamentos…
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-6 text-slate-500 text-xs border border-dashed border-slate-300 rounded-sm">
              Nenhuma loja cadastrada. Todos os pedidos vão cair em <b>Desconhecido</b>.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-600 border-b border-slate-200">
                  <th className="px-2 py-2 text-left">Nome no Bling</th>
                  <th className="px-2 py-2 text-left w-32">ID da loja</th>
                  <th className="px-2 py-2 text-left w-40">Marketplace</th>
                  <th className="px-2 py-2 text-right w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mappings.map((m) => (
                  <tr key={m.id}>
                    <td className="px-2 py-2.5 font-mono text-sm text-slate-800">
                      {m.store_name || <span className="text-slate-400 italic text-xs">— só por ID —</span>}
                    </td>
                    <td className="px-2 py-2.5 font-mono text-xs text-slate-700">
                      {m.store_id || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2.5">
                      <span className="inline-block bg-slate-100 px-2 py-0.5 rounded-sm text-xs font-bold text-slate-700">
                        {m.marketplace}
                      </span>
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <button
                        data-testid="bling-store-mapping-delete"
                        onClick={() => deleteMapping(m.id, m.store_name || m.store_id)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Remover"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-slate-900"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Failures Dialog — lists orders with failed invoicing, allows manual retry.
// ---------------------------------------------------------------------------
function FailuresDialog({ open, onClose, data, loading, onRefresh, onRetry, onDismiss, onPurgeAll, purging, retryingId }) {
  if (!open) return null;
  const items = data.items || [];
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };
  const handlePurgeAll = () => {
    if (!onPurgeAll || items.length === 0) return;
    const ok = window.confirm(
      `Verificar as ${items.length} falha(s) no Bling e limpar automaticamente as que já estão AUTORIZADAS?\n\n` +
      `Pedidos com NF-e não-autorizada permanecerão na lista para ação manual.`
    );
    if (!ok) return;
    onPurgeAll({ force: false });
  };
  const handlePurgeForce = () => {
    if (!onPurgeAll || items.length === 0) return;
    const ok = window.confirm(
      `⚠️ Remover TODAS as ${items.length} falhas da lista (mesmo as não autorizadas no Bling)?\n\n` +
      `Use somente se você já resolveu tudo manualmente no Bling.\n` +
      `Esta ação não pode ser desfeita.`
    );
    if (!ok) return;
    onPurgeAll({ force: true });
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bling-failures-backdrop"
    >
      <div
        className="bg-white w-full max-w-6xl max-h-[90vh] flex flex-col rounded-md shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="bling-failures-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-700" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-900">
              Falhas no Faturamento
            </h2>
            <span className="text-[10px] font-mono text-slate-500 ml-2">
              retry: 5m → 15m → 45m → 2h → 6h
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="bling-failures-purge-all"
              onClick={handlePurgeAll}
              disabled={purging || loading || items.length === 0}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-emerald-700 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 rounded-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Consulta cada NF-e no Bling e limpa as que já estão AUTORIZADAS"
            >
              {purging ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3 h-3" />
              )}
              Sincronizar & limpar
            </button>
            <button
              data-testid="bling-failures-purge-force"
              onClick={handlePurgeForce}
              disabled={purging || loading || items.length === 0}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-red-300 bg-white hover:bg-red-50 text-red-800 rounded-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Remove TODAS as falhas da lista, ignorando o status no Bling"
            >
              <Trash2 className="w-3 h-3" />
              Limpar tudo
            </button>
            <button
              data-testid="bling-failures-refresh"
              onClick={onRefresh}
              disabled={loading}
              className="text-slate-500 hover:text-slate-900 p-1.5 disabled:opacity-50"
              title="Atualizar"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            <button
              data-testid="bling-failures-close"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-900 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              Nenhuma falha registrada. Tudo funcionando perfeitamente.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-slate-200 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Nº Pedido</th>
                  <th className="px-3 py-2 text-left">Marketplace</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center">Tentativas</th>
                  <th className="px-3 py-2 text-left">Próxima tentativa</th>
                  <th className="px-3 py-2 text-left">Último erro</th>
                  <th className="px-3 py-2 text-right w-40">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-testid="bling-failures-tbody">
                {items.map((it) => {
                  const needsManual = !!it.needs_manual;
                  return (
                    <tr key={it.order_id} className={needsManual ? "bg-red-50/40" : ""}>
                      <td className="px-3 py-2">
                        {needsManual ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-red-100 text-red-800 rounded-sm">
                            AÇÃO MANUAL
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm">
                            <RotateCw className="w-2.5 h-2.5" />
                            Retentando
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <div className="font-bold text-slate-900 text-sm" title={`Bling #${it.numero || it.order_id}`}>
                          {it.numero_loja ? (
                            <>
                              <span className="text-slate-900">{it.numero_loja}</span>
                              <span className="ml-1 text-[10px] font-normal uppercase tracking-widest text-slate-400">
                                (mkt)
                              </span>
                            </>
                          ) : (
                            `#${it.numero || it.order_id}`
                          )}
                        </div>
                        {it.numero_loja && it.numero && (
                          <div className="text-[10px] text-slate-500">
                            Bling #{it.numero}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-slate-100 text-slate-700">
                          {it.marketplace || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm">
                        {brl(Number(it.valor_total || 0))}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`font-mono text-xs font-bold ${needsManual ? "text-red-700" : "text-amber-700"}`}>
                          {it.attempts}/{data.max_attempts}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[11px] font-mono text-slate-600">
                        {needsManual ? "—" : fmtDate(it.next_retry_at)}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-red-800 max-w-[260px] truncate" title={it.last_error}>
                        {it.last_error || "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            data-testid={`bling-failure-retry-${it.order_id}`}
                            onClick={() => onRetry(it.order_id)}
                            disabled={retryingId === it.order_id}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-emerald-700 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 rounded-sm inline-flex items-center gap-1 disabled:opacity-60"
                          >
                            {retryingId === it.order_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <RotateCw className="w-3 h-3" />
                            )}
                            Retentar
                          </button>
                          <button
                            data-testid={`bling-failure-dismiss-${it.order_id}`}
                            onClick={() => onDismiss(it.order_id)}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 rounded-sm inline-flex items-center gap-1"
                            title="Descartar (após resolver manualmente no Bling)"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-between text-xs">
          <div className="text-slate-600">
            <b className="text-red-800">{data.needs_manual_count || 0}</b> manual ·{" "}
            <b className="text-amber-800">{data.retrying_count || 0}</b> retentando
          </div>
          <div className="text-[10px] font-mono text-slate-500">
            &quot;Retentar&quot; força uma nova tentativa imediata · &quot;Descartar&quot; apenas remove da lista
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Start-Confirm Card — inline card shown when user toggles auto-billing ON.
// Two choices: SIM (start now) or "Configurar agora" (open rules dialog).
// ---------------------------------------------------------------------------
function StartConfirmCard({ config, onConfirm, onConfigure, onCancel }) {
  const rules = (config.rules || []).filter((r) => r.enabled);
  return (
    <div
      className="border-2 border-emerald-300 bg-emerald-50 rounded-xl p-4"
      data-testid="bling-auto-start-confirm"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-black text-emerald-900 uppercase tracking-wider">
            Iniciar Faturamento Automático agora?
          </h3>
          <p className="text-xs text-emerald-800 mt-1">
            Ao confirmar, o sistema começa a aplicar descontos e emitir NF-e para
            todos os pedidos elegíveis em <b>Em Aberto</b>. O ciclo automático continua
            rodando <b>24h/dia, a cada 2 min</b>, mesmo se você fechar o navegador.
          </p>

          {rules.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="bling-auto-confirm-rules">
              {rules.map((r) => (
                <span
                  key={r.marketplace}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-white border border-emerald-300 text-emerald-900 rounded-sm"
                >
                  {r.marketplace}
                  <span className="font-mono text-emerald-700">
                    {Number(r.discount_pct || 0).toFixed(2).replace(".", ",")}%
                  </span>
                </span>
              ))}
            </div>
          )}

          {config.skip_discount_for_cnpj && (
            <div className="text-[10px] text-emerald-800 mt-2 font-mono">
              · CNPJ (PJ): sem desconto
            </div>
          )}

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              data-testid="bling-auto-confirm-yes"
              onClick={onConfirm}
              className="px-5 py-2.5 text-xs font-black uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm inline-flex items-center gap-2 shadow-sm"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              SIM, iniciar agora
            </button>
            <button
              data-testid="bling-auto-confirm-configure"
              onClick={onConfigure}
              className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider border border-emerald-700 bg-white hover:bg-emerald-50 text-emerald-900 rounded-sm inline-flex items-center gap-2"
            >
              <Settings className="w-3.5 h-3.5" />
              Configurar agora
            </button>
            <button
              data-testid="bling-auto-confirm-cancel"
              onClick={onCancel}
              className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Copy-config dialog — user picks a SOURCE Bling on this account and clones
// its auto-billing config into the currently-selected Bling. Solves the
// "new Bling added but auto-billing doesn't work" scenario when the OAuth
// auto-copy didn't run (pre-fix connections like Aeromix in production).
// ---------------------------------------------------------------------------
function CopyConfigDialog({
  onClose, blings, currentBling, companies, sourceId, setSourceId,
  overwrite, setOverwrite, onConfirm, loading, hasExistingConfig,
}) {
  const siblings = (blings || []).filter((b) => b.id !== currentBling?.id);
  const labelOf = (b) => {
    const companyName = companies.find((c) => c.id === b.company_id)?.nome_fantasia;
    return blingLabel(b) || companyName || b.account_label || `Bling ${b.id.slice(0, 6)}`;
  };
  const targetLabel = currentBling ? labelOf(currentBling) : "—";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
      data-testid="bling-copy-config-dialog"
    >
      <div
        className="bg-white border-2 border-slate-200 w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Faturamento Automático
            </div>
            <div className="text-lg font-black text-slate-900">
              Copiar de outro Bling
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-600 leading-relaxed">
            Clona as regras, o toggle &quot;pular desconto PJ&quot; e o status ON/OFF do
            Bling de origem para <b className="font-mono">{targetLabel}</b>.
            Útil quando um Bling foi adicionado depois e não tem configuração
            própria — ele passa a operar exatamente como o de origem.
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Origem
            </label>
            <select
              data-testid="bling-copy-source-select"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border-2 border-slate-200 bg-white focus:outline-none focus:border-[#002EA6]"
            >
              <option value="" disabled>Escolha o Bling de origem…</option>
              {siblings.map((b) => (
                <option key={b.id} value={b.id}>{labelOf(b)}</option>
              ))}
            </select>
            {siblings.length === 0 && (
              <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                Este é o único Bling desta conta — não há origem para copiar.
              </div>
            )}
          </div>

          {hasExistingConfig && (
            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                data-testid="bling-copy-overwrite"
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Este Bling já tem uma configuração. Marque para <b>substituir</b>{" "}
                (as regras e o status atual serão perdidos).
              </span>
            </label>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900"
          >
            Cancelar
          </button>
          <button
            data-testid="bling-copy-confirm"
            onClick={onConfirm}
            disabled={loading || !sourceId || siblings.length === 0}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-white bg-[#002EA6] hover:bg-[#001A66] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
            Copiar configuração
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Diagnostics dialog — reads /bling/auto-billing/diagnostics for the active
// Bling and shows a per-order verdict so the user can see EXACTLY why the
// robot skipped every pedido on the last run (missing rule, disabled
// marketplace, wrong situação, already invoiced, etc.).
// ---------------------------------------------------------------------------
function DiagnosticsDialog({ onClose, loading, data, integration }) {
  const verdictLabel = {
    would_discount_then_invoice: { text: "Aplicaria desconto + faturaria", color: "text-emerald-700 bg-emerald-50" },
    would_invoice: { text: "Faturaria", color: "text-emerald-700 bg-emerald-50" },
    skip_no_rule: { text: "Sem regra p/ marketplace", color: "text-amber-800 bg-amber-50" },
    skip_rule_disabled: { text: "Regra desativada", color: "text-amber-800 bg-amber-50" },
    skip_already_invoiced: { text: "Já faturado", color: "text-slate-600 bg-slate-100" },
    skip_backoff: { text: "Aguardando retry", color: "text-orange-800 bg-orange-50" },
    skip_needs_manual: { text: "Ação manual", color: "text-red-700 bg-red-50" },
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
      data-testid="bling-diagnostics-dialog"
    >
      <div
        className="bg-white border-2 border-slate-200 w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Diagnóstico do Robô
            </div>
            <div className="text-lg font-black text-slate-900">
              Por que o faturamento automático não rodou?
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {loading && (
            <div className="text-center py-10 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Simulando uma passagem do robô…
            </div>
          )}

          {!loading && data && (
            <>
              {/* Config source card */}
              <div className="border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Configuração carregada
                </div>
                <div className="text-xs space-y-1">
                  <div>
                    <span className="text-slate-500">Fonte da config:</span>{" "}
                    <span className="font-mono font-bold">
                      {data.config_loaded?.source === "per_bling" && (
                        <span className="text-emerald-700">
                          per_bling (correta — este Bling tem regras próprias)
                        </span>
                      )}
                      {data.config_loaded?.source === "tenant_legacy" && (
                        <span className="text-amber-700">
                          tenant_legacy (fallback — clique em &quot;Copiar de outro Bling&quot; para criar uma config própria)
                        </span>
                      )}
                      {data.config_loaded?.source === "default_disabled" && (
                        <span className="text-red-700">
                          default_disabled (nada configurado — auto-billing OFF por padrão)
                        </span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Status:</span>{" "}
                    <span className={`font-bold ${data.config_loaded?.enabled ? "text-emerald-700" : "text-slate-500"}`}>
                      {data.config_loaded?.enabled ? "ATIVO" : "DESLIGADO"}
                    </span>
                    {" · "}
                    <span className="text-slate-500">Skip PJ:</span>{" "}
                    <span className="font-bold">{data.config_loaded?.skip_discount_for_cnpj ? "SIM" : "NÃO"}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Regras:</span>{" "}
                    <span className="font-mono">
                      {(data.config_loaded?.rules || []).filter((r) => r.enabled).length} ativas /{" "}
                      {(data.config_loaded?.rules || []).length} totais
                    </span>
                  </div>
                </div>
              </div>

              {/* Live fetch card */}
              <div className="border border-slate-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                  Pedidos encontrados agora
                </div>
                {data.live_fetch?.fetch_error ? (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">
                    Erro ao buscar pedidos no Bling: <span className="font-mono">{data.live_fetch.fetch_error}</span>
                  </div>
                ) : (
                  <>
                    <div className="text-xs">
                      <b>{data.live_fetch?.orders_found || 0}</b> pedido(s) elegíveis
                    </div>
                    {data.live_fetch?.marketplace_breakdown && Object.keys(data.live_fetch.marketplace_breakdown).length > 0 && (
                      <div className="mt-2 text-xs">
                        <span className="text-slate-500">Por marketplace:</span>{" "}
                        {Object.entries(data.live_fetch.marketplace_breakdown).map(([mkt, count]) => (
                          <span key={mkt} className="inline-block bg-slate-100 px-2 py-0.5 mr-1 rounded-sm font-mono">
                            {mkt}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                    {data.live_fetch?.verdict_summary && Object.keys(data.live_fetch.verdict_summary).length > 0 && (
                      <div className="mt-2 text-xs">
                        <span className="text-slate-500">Veredito do robô:</span>{" "}
                        {Object.entries(data.live_fetch.verdict_summary).map(([v, count]) => (
                          <span
                            key={v}
                            className={`inline-block px-2 py-0.5 mr-1 rounded-sm font-mono ${verdictLabel[v]?.color || "bg-slate-100"}`}
                          >
                            {verdictLabel[v]?.text || v}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Per-order verdicts */}
              {(data.live_fetch?.orders || []).length > 0 && (
                <div className="border border-slate-200 rounded-sm">
                  <div className="px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-50">
                    Análise por pedido
                    {data.live_fetch?.orders_truncated && " (mostrando os primeiros 100)"}
                  </div>
                  <div className="divide-y divide-slate-100">
                    {data.live_fetch.orders.map((o) => (
                      <div key={o.order_id} className="px-3 py-2 text-xs flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono font-bold">
                            #{o.numero} · {o.marketplace}
                            {o.is_cnpj && (
                              <span className="ml-2 text-[9px] uppercase tracking-widest bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded-sm">CNPJ</span>
                            )}
                          </div>
                          <div className="text-slate-500 truncate">
                            Situação: {o.situacao_nome || o.situacao_valor || "—"} · R$ {(o.total || 0).toFixed(2)}
                          </div>
                          <div className="text-slate-700 mt-0.5">{o.reason}</div>
                        </div>
                        <span
                          className={`inline-block text-[9px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm shrink-0 ${verdictLabel[o.verdict]?.color || "bg-slate-100 text-slate-600"}`}
                        >
                          {verdictLabel[o.verdict]?.text || o.verdict}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-slate-900"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
